import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { StorageService } from '../../services/storage-service';
import { AuthService } from '../../services/auth-service';
import { BehaviorSubject, throwError, from } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';

let isRefreshing = false;
const refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const storage = inject(StorageService);
  const authService = inject(AuthService);

  // Skip login & token refresh requests
  if (req.url.includes('oauth2/access_token') || req.url.includes('/o/token')) {
    return next(req);
  }

  const token = storage.get('access_token');
  const tokenType = storage.get('token_type');

  let authReq = req;
  // If no token, just pass request through without auth header
  if (token) {
    authReq = req.clone({
      setHeaders: {
        Authorization: `${tokenType ?? 'JWT'} ${token}`,
      },
    });
  }

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 && token) {
        if (!isRefreshing) {
          isRefreshing = true;
          refreshTokenSubject.next(null);

          return from(authService.refreshToken()).pipe(
            switchMap((tokenData) => {
              isRefreshing = false;
              if (tokenData && tokenData.access_token) {
                refreshTokenSubject.next(tokenData.access_token);
                return next(
                  req.clone({
                    setHeaders: {
                      Authorization: `${tokenData.token_type ?? 'JWT'} ${tokenData.access_token}`,
                    },
                  })
                );
              } else {
                return throwError(() => error);
              }
            }),
            catchError((err) => {
              isRefreshing = false;
              return throwError(() => err);
            })
          );
        } else {
          return refreshTokenSubject.pipe(
            filter((result) => result !== null),
            take(1),
            switchMap((newToken) => {
              const currentTokenType = storage.get('token_type') ?? 'JWT';
              return next(
                req.clone({
                  setHeaders: {
                    Authorization: `${currentTokenType} ${newToken}`,
                  },
                })
              );
            })
          );
        }
      }
      return throwError(() => error);
    })
  );
};
